/**
 * @file useOutsideHaulierPoh.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-26
 * @modified 2026-10-26
 * @description "Raise and send a PO-H?" on the transport planning boards
 *              (TransportPlanning and HaulageJobsPlanning) after an outside
 *              haulier is set on a row — the haulier cell, a driver bringing
 *              their haulier, the bulk edit, or rows dragged onto another
 *              haulier's group (Cam, 26 Oct 2026). Called once the board's
 *              own haulier save has succeeded; each order is asked once per
 *              change (utils/outsideHaulierPoh), one window at a time.
 *
 *              Usage:
 *                const outsidePoh = useOutsideHaulierPoh({ user, date, enabled, onChanged, showAlert });
 *                after a haulier save: outsidePoh.afterHaulierChange(rows, haulier)
 *                render: {outsidePoh.element}
 */

import React, { useCallback, useRef, useState } from 'react';
import OutsideHaulierPohDialog from '../components/OutsideHaulierPohDialog';
import { ordersToAsk } from '../utils/outsideHaulierPoh';

/**
 * @param {object} o
 * @param {object} o.user
 * @param {string} o.date          the board's day (YYYY-MM-DD)
 * @param {boolean} o.enabled      the user may plan this day (not view only, not past, not locked)
 * @param {Function} [o.onChanged] the order changed (the board re-reads)
 * @param {Function} [o.showAlert] ({ title, message, type }) — what was done, once the windows close
 */
export default function useOutsideHaulierPoh({ user, date, enabled, onChanged, showAlert }) {
  const [queue, setQueue] = useState([]);
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  const afterHaulierChange = useCallback((rows, haulier) => {
    // Our own haulier or none still runs, so the order is asked again next time.
    const asks = ordersToAsk(rows, haulier);
    if (!enabledRef.current || !asks.length) return;
    setQueue(prev => [...prev, ...asks.map(a => ({ ...a, date }))]);
  }, [date]);

  const current = queue[0] || null;
  const done = useCallback((changed, notes = []) => {
    setQueue(prev => prev.slice(1));
    if (changed) {
      if (onChanged) onChanged();
      if (showAlert && notes.length) {
        showAlert({ title: 'PO-H raised', message: notes.join(' '), type: 'success' });
      }
    }
  }, [onChanged, showAlert]);

  const element = current ? (
    <OutsideHaulierPohDialog
      key={`${current.orderId}:${current.haulier}`}
      request={current}
      user={user}
      onClose={done}
    />
  ) : null;

  return { afterHaulierChange, element, pending: queue.length };
}
